import React, { useEffect, useState } from "react";
import Header from "./components/Header";
import MainContent from "./components/MainContent";
import "./App.css";
import LeftSidebar from "./components/LeftSidebar";
import RightSidebar from "./components/RightSidebar";
import "bootstrap/dist/css/bootstrap.min.css";
import { InlineStyle, Template, TemplateColumn } from "./components/interfaces/template.interface";
import { schemaFields, specificStyles } from "./utils/schema.fields";
import { TripType, ServiceType } from "@industronics/fids-utils";
import { ActionType } from "./utils/common.utils";

const App: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<Template>();
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string>("");
  const [isHeader, setIsHeader] = useState<boolean>(false);
  const [selectedFontColor, setSelectedFontColor] = useState<string>("#FFFFFF");
  const [selectedBgColor, setSelectedBgColor] = useState<string>("#FFFFFF");
  const [selectedPageBgColor, setSelectedPageBgColor] = useState<string>("");
  const [cellLabel, setCellLabel] = useState<string | null>(null);
  const [selectedFontSize, setSelectedFontSize] = useState<string>("23px");
  const [selectedDateFormat, setSelectedDateFormat] = useState<string>("DD MMM");
  const [selectedRemarkKey, setSelectedRemarkKey] = useState<string | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const [selectedTextAlignment, setSelectedTextAlignment] = useState<string>("center");
  const [actionType, setActionType] = useState<ActionType>("Create");
  const [animationDirection, setAnimationDirection] = useState("up");
  const [flipSpeed, setFlipSpeed] = useState("normal");
  const [selectedColumnWidth, setSelectedColumnWidth] = useState<string>('');
  const [selectedFontWeight, setSelectedFontWeight] = useState<string>('400');
  const [selectedFontFamily, setSelectedFontFamily] = useState<string>('sans-serif');

  useEffect(() => {
    if (selectedTemplate?.draft?.pageFontSize) {
      setSelectedFontSize(selectedTemplate.draft.pageFontSize);
    }
  }, [selectedTemplate?.draft?.pageFontSize]);

  const handleItemClick = (
    rowIndex: number,
    isHeader: boolean,
    col: TemplateColumn,
    field: string,
    styles: InlineStyle[]
  ) => {
    setSelectedRowIndex(rowIndex);
    setSelectedField(field);
    setIsHeader(isHeader);
    setSelectedLabel(col?.header?.label);
    setSelectedRemarkKey(null);

    if (rowIndex === -1) {
      const textAlgin = col.header.cssClass
        .split(" ")
        .find((x) => x === "text-start" || x === "text-end" || x === "text-center");
      if (textAlgin) setSelectedTextAlignment(textAlgin.replace("text-", ""));
      else setSelectedTextAlignment("center");
    }

    const fontColor = styles.find((x) => x.prop === "color");
    if (fontColor) {
      if (rowIndex % 2 === 0) setSelectedFontColor(selectedTemplate?.draft?.alternateRow?.color || "initial");
      else setSelectedFontColor(fontColor.value);
    }

    const bgColor = styles.find((x) => x.prop === "background-color");
    const bodyBgColor = selectedTemplate?.draft?.body?.inLineStyle?.find((x) => x.prop === "background-color");
    if (bgColor || bodyBgColor) {
      if (rowIndex % 2 === 0) setSelectedBgColor(selectedTemplate?.draft?.alternateRow?.background || "initial");
      else setSelectedBgColor(bgColor?.value || bodyBgColor?.value || "initial");
    }

    const pageBgColor = selectedTemplate?.draft?.body?.inLineStyle?.find((x) => x.prop === "background-color");
    if (pageBgColor) setSelectedPageBgColor(pageBgColor.value);

    if (isHeader) {
      const width = col.header?.inLineStyle?.find(s => s.prop === 'width')?.value || '';
      setSelectedColumnWidth(width);
    }

    const fontWeight = styles.find(s => s.prop === 'font-weight');
    setSelectedFontWeight(fontWeight?.value || '400');

    const fontFamily = styles.find(s => s.prop === 'font-family');
    setSelectedFontFamily(fontFamily?.value || 'sans-serif');

  };

  const onFontColorChange = (color: string) => {
    const field = isHeader ? "header" : "body";
    setSelectedFontColor(color);

    if (selectedRowIndex > -1 && selectedRowIndex % 2 === 0 && !cellLabel && !selectedRemarkKey) {
      return onAlternateRowFontColorChange(color);
    }

    // ---- FIX: update draft.columns (not template.columns) ----
    if (selectedTemplate?.draft?.columns && !cellLabel && !selectedRemarkKey) {
      const updatedTemplate: Template = {
        ...selectedTemplate,
        draft: {
          ...selectedTemplate.draft,
          columns: selectedTemplate.draft.columns.map((col) => {
            const fontColor = col[field]?.inLineStyle?.find((style) => style.prop === "color");

            const updatedStyles = fontColor
              ? col[field].inLineStyle!.map((style) =>
                style.prop === "color" ? { ...style, value: color } : style
              )
              : [...(col[field].inLineStyle || []), { prop: "color", value: color }];

            return {
              ...col,
              [field]: {
                ...col[field],
                inLineStyle: updatedStyles,
              },
            };
          }),
        },
      };

      setSelectedTemplate(updatedTemplate);
    }
    // ---- FIX: update draft.columns[index] ----
    else if (selectedTemplate?.draft?.columns && cellLabel && !selectedRemarkKey) {
      const index = selectedTemplate.draft.columns.findIndex((col) => col.field === cellLabel);
      if (index === -1) return;

      const updatedTemplate: Template = {
        ...selectedTemplate,
        draft: {
          ...selectedTemplate.draft,
          columns: selectedTemplate.draft.columns.map((c, i) => {
            if (i !== index) return c;

            const col: any = { ...c };
            if (!col.body) col.body = {};
            if (!col.body.date) col.body.date = {};
            if (!col.body.date.inLineStyle) col.body.date.inLineStyle = [];

            const fontColor = col.body.date.inLineStyle.find((style: any) => style.prop === "color");
            col.body.date.inLineStyle = fontColor
              ? col.body.date.inLineStyle.map((style: any) =>
                style.prop === "color" ? { ...style, value: color } : style
              )
              : [...col.body.date.inLineStyle, { prop: "color", value: color }];

            return col;
          }),
        },
      };

      setSelectedTemplate(updatedTemplate);
    }
    // ---- FIX: remark specific styles should update draft.columns too ----
    else if (selectedTemplate && selectedRemarkKey) {
      const updatedTemplate: Template = {
        ...selectedTemplate,
        draft: {
          ...selectedTemplate.draft!,
          columns: selectedTemplate.draft!.columns.map((col) => {
            if (col.field === "remarkCode" && col.body?.specificStyles) {
              const ss: any = col.body.specificStyles || {};

              if (!ss[selectedRemarkKey]) ss[selectedRemarkKey] = { inLineStyle: [] };

              const fontColorStyle = ss[selectedRemarkKey].inLineStyle.find((style: any) => style.prop === "color");
              if (fontColorStyle) fontColorStyle.value = color;
              else ss[selectedRemarkKey].inLineStyle.push({ prop: "color", value: color });

              return {
                ...col,
                body: { ...col.body, specificStyles: ss },
              };
            }
            return col;
          }),
        },
      };

      setSelectedTemplate(updatedTemplate);
    }
  };

  const onBgColorChange = (color: string) => {
    setSelectedBgColor(color);

    if (selectedRowIndex > -1 && selectedRowIndex % 2 === 0 && !cellLabel && !selectedRemarkKey) {
      return onAlternateRowBgColorChange(color);
    }

    const field = isHeader ? "header" : "body";

    if (selectedTemplate?.draft?.columns) {
      const updatedTemplate: Template = {
        ...selectedTemplate,
        draft: {
          ...selectedTemplate.draft,
          columns: selectedTemplate.draft.columns.map((col) => {
            const bgColor = col[field].inLineStyle?.find((style) => style.prop === "background-color");

            const updatedStyles = bgColor
              ? col[field].inLineStyle!.map((style) =>
                style.prop === "background-color" ? { ...style, value: color } : style
              )
              : [...(col[field].inLineStyle || []), { prop: "background-color", value: color }];

            return {
              ...col,
              [field]: {
                ...col[field],
                inLineStyle: updatedStyles,
              },
            };
          }),
        },
      };

      setSelectedTemplate(updatedTemplate);
    }
  };

  const onLabelChange = (label: string) => {
    if (!selectedTemplate || !selectedField || !selectedTemplate.draft?.columns) return;

    const index = selectedTemplate.draft.columns.findIndex((col) => col.field === selectedField);
    if (index === -1) return;

    const currentLabel = selectedTemplate.draft.columns[index].header.label;
    if (currentLabel === label) return;

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        columns: selectedTemplate.draft.columns.map((c, i) =>
          i === index ? { ...c, header: { ...c.header, label } } : c
        ),
      },
    };

    setSelectedLabel(label);
    setSelectedTemplate(updatedTemplate);
  };

  const onPageBgColorChange = (color: string) => {
    if (!selectedTemplate?.draft) return;

    const updatedStyles = selectedTemplate.draft.body?.inLineStyle
      ? selectedTemplate.draft.body.inLineStyle.map((style) =>
        style.prop === "background-color" ? { ...style, value: color } : style
      )
      : [{ prop: "background-color", value: color }];

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        body: {
          ...(selectedTemplate.draft.body || {}),
          inLineStyle: updatedStyles,
        },
      },
    };

    setSelectedTemplate(updatedTemplate);
    setSelectedPageBgColor(color);
  };

  const onAlternateRowBgColorChange = (color: string) => {
    if (!selectedTemplate?.draft) return;

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        alternateRow: {
          ...(selectedTemplate.draft.alternateRow || { background: "", color: "" }),
          background: color,
        },
      },
    };
    setSelectedTemplate(updatedTemplate);
  };

  const onAlternateRowFontColorChange = (color: string) => {
    if (!selectedTemplate?.draft) return;

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        alternateRow: {
          ...(selectedTemplate.draft.alternateRow || { background: "", color: "" }),
          color,
        },
      },
    };
    setSelectedTemplate(updatedTemplate);
  };

  const onCellClick = (col: TemplateColumn) => {
    setCellLabel(col.field as any);
  };

  const onDateFormatChange = (format: string) => {
    setSelectedDateFormat(format);

    if (!selectedTemplate?.draft?.columns) return;

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        columns: selectedTemplate.draft.columns.map((col) => {
          if (col.type !== "date") return col;

          return {
            ...col,
            body: {
              ...col.body,
              date: {
                // keep existing values if present
                ...(col.body?.date || {}),
                // REQUIRED fields
                format,
                cssClass: col.body?.date?.cssClass ?? "p-1",
                // keep inline styles if any
                inLineStyle: col.body?.date?.inLineStyle ?? [],
              },
            },
          };
        }),
      },
    };

    setSelectedTemplate(updatedTemplate);
  };

  const updateTemplate = (updatedTemplate: Template) => {
    setSelectedTemplate(updatedTemplate);
  };

  const handleColumnsChange = (updatedColumns: string[]) => {
    if (!selectedTemplate?.draft?.columns) return;

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        columns: updatedColumns.map((field) => {
          const existingColumn = selectedTemplate.draft!.columns.find((col: TemplateColumn) => col.field === (field as any));
          if (existingColumn) return existingColumn;

          const schemaFieldProp = schemaFields.find((x) => x.field === field);
          const headerInLineStyle = selectedTemplate.draft!.columns[0]?.header?.inLineStyle || [];
          const bodyInLineStyle = selectedTemplate.draft!.columns[0]?.body?.inLineStyle || [];

          let label = schemaFieldProp?.label;

          if (field === "scheduledTravelTime") {
            label = selectedTemplate.tripType === TripType.Departure ? "STD" : "STA";
          } else if (field === "ETA") {
            label = selectedTemplate.tripType === TripType.Departure ? "ETD" : "ETA";
          } else if (field === "stations") {
            label = selectedTemplate.tripType === TripType.Departure ? "To" : "From";
          }

          return {
            field: field as any,
            type: schemaFieldProp ? schemaFieldProp.type : null,
            header: {
              label: label as any,
              cssClass: "p-1 text-left flight",
              inLineStyle: headerInLineStyle,
            },
            body: {
              showLogo: true,
              cssClass: "p-1",
              inLineStyle: bodyInLineStyle,
              specificStyles: field === "remarkCode" ? specificStyles : {}, // ✅ object
            },
          } as any;
        }),
      },
    };

    setSelectedTemplate(updatedTemplate);
  };

  const onRemarkCellClick = (styles: InlineStyle[], key: string) => {
    setSelectedRemarkKey(key);

    const fontColor = styles.find((x) => x.prop === "color");
    if (fontColor) setSelectedFontColor(fontColor.value);
    else setSelectedFontColor("#FFFFFF");

    // FIX: you were setting font color with font-size by mistake
    const fontSize = styles.find((x) => x.prop === "font-size");
    if (fontSize) setSelectedFontSize(fontSize.value);
    else setSelectedFontSize("20px");
  };

  const showFlightsInlineToggle = () => {
    if (!selectedTemplate?.draft?.columns) return;

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        columns: selectedTemplate.draft.columns.map((col) => {
          if (col.field === "flightNumber") {
            return {
              ...col,
              body: {
                ...col.body,
                showFlightsInline: !col.body.showFlightsInline,
              },
            };
          }
          return col;
        }),
      },
    };

    setSelectedTemplate(updatedTemplate);
  };

  const onFontSizeChange = (size: string) => {
    if (!selectedTemplate?.draft) return;

    setSelectedFontSize(size);

    // Normal: set draft.pageFontSize
    if (!selectedRemarkKey) {
      const updatedTemplate: Template = {
        ...selectedTemplate,
        draft: {
          ...selectedTemplate.draft,
          pageFontSize: size,
        },
      };
      setSelectedTemplate(updatedTemplate);
    } else {
      // Remark: set font-size across specificStyles
      const updatedTemplate: Template = {
        ...selectedTemplate,
        draft: {
          ...selectedTemplate.draft,
          columns: selectedTemplate.draft.columns.map((col) => {
            if (col.field === "remarkCode" && col.body?.specificStyles) {
              const ss: any = col.body.specificStyles || {};
              Object.keys(ss).forEach((k) => {
                if (!ss[k]) ss[k] = { inLineStyle: [] };

                const fontSizeStyle = ss[k].inLineStyle.find((style: any) => style.prop === "font-size");
                if (fontSizeStyle) fontSizeStyle.value = size;
                else ss[k].inLineStyle.push({ prop: "font-size", value: size });
              });

              return {
                ...col,
                body: { ...col.body, specificStyles: ss },
              };
            }
            return col;
          }),
        },
      };

      setSelectedTemplate(updatedTemplate);
    }
  };

  const onPageClick = () => { };

  const onTextAlign = (alignment: string) => {
    if (!selectedField || !selectedTemplate?.draft?.columns) {
      console.error("No selected field or template available.");
      return;
    }

    setSelectedTextAlignment(alignment);

    const columnIndex = selectedTemplate.draft.columns.findIndex((col) => col.field === (selectedField as any));
    if (columnIndex === -1) {
      console.error("Selected field not found in template columns.");
      return;
    }

    const updatedColumns = selectedTemplate.draft.columns.map((col, idx) => {
      if (idx !== columnIndex) return col;

      return {
        ...col,
        header: {
          ...col.header,
          cssClass: `text-${alignment} ${col.header?.cssClass?.replace(/text-(start|center|end)/g, "")}`,
        },
        body: {
          ...col.body,
          cssClass: `text-${alignment} ${col.body?.cssClass?.replace(/text-(start|center|end)/g, "")}`,
        },
      };
    });

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: { ...selectedTemplate.draft, columns: updatedColumns },
    };

    setSelectedTemplate(updatedTemplate);
  };

  const onColumnWidthChange = (width: string) => {
    if (!selectedField || !selectedTemplate?.draft?.columns) return;

    setSelectedColumnWidth(width);

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        columns: selectedTemplate.draft.columns.map(col => {
          if (col.field !== selectedField) return col;

          const existingStyles = col.header?.inLineStyle ?? [];
          const hasWidth = existingStyles.some(s => s.prop === 'width');

          return {
            ...col,
            header: {
              ...col.header,
              inLineStyle: hasWidth
                ? existingStyles.map(s => s.prop === 'width' ? { ...s, value: width } : s)
                : [...existingStyles, { prop: 'width', value: width }],
            },
          };
        }),
      },
    };

    setSelectedTemplate(updatedTemplate);
  }

  const onFontWeightChange = (weight: string) => {
    if (!selectedTemplate?.draft?.columns) return;
    setSelectedFontWeight(weight);

    const field = isHeader ? 'header' : 'body';

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        columns: selectedTemplate.draft.columns.map((col) => {
          if (selectedField && col.field !== selectedField) return col;

          const existingStyles = col[field]?.inLineStyle ?? [];
          const hasWeight = existingStyles.some(s => s.prop === 'font-weight');

          return {
            ...col,
            [field]: {
              ...col[field],
              inLineStyle: hasWeight
                ? existingStyles.map(s => s.prop === 'font-weight' ? { ...s, value: weight } : s)
                : [...existingStyles, { prop: 'font-weight', value: weight }],
            },
          };
        }),
      },
    };

    setSelectedTemplate(updatedTemplate);
  };

  const onFontFamilyChange = (family: string) => {
    if (!selectedTemplate?.draft?.columns) return;
    setSelectedFontFamily(family);

    const field = isHeader ? 'header' : 'body';

    const updatedTemplate: Template = {
      ...selectedTemplate,
      draft: {
        ...selectedTemplate.draft,
        columns: selectedTemplate.draft.columns.map((col) => {
          if (selectedField && col.field !== selectedField) return col;

          const existingStyles = col[field]?.inLineStyle ?? [];
          const hasFamily = existingStyles.some(s => s.prop === 'font-family');

          return {
            ...col,
            [field]: {
              ...col[field],
              inLineStyle: hasFamily
                ? existingStyles.map(s => s.prop === 'font-family' ? { ...s, value: family } : s)
                : [...existingStyles, { prop: 'font-family', value: family }],
            },
          };
        }),
      },
    };

    setSelectedTemplate(updatedTemplate);
  };

  return (
    <div className="app-container">
      <Header
        onAction={(action: ActionType) => {
          setActionType(action);
        }}
        selectedTemplate={selectedTemplate}
      />

      <div className="app-body">
        <LeftSidebar
          actionType={actionType}
          onTemplateSelect={(template: Template) => {
            setSelectedTemplate(JSON.parse(JSON.stringify(template)));
          }}
        />

        <MainContent
          onPageClick={onPageClick}
          updateTemplate={updateTemplate}
          onCellClick={onCellClick}
          onRemarkCellClick={onRemarkCellClick}
          onBodyRowClick={(index: number) => {
            setIsHeader(false);
            setCellLabel(null);
            setSelectedRowIndex(index);
          }}
          template={selectedTemplate!}
          onItemClick={handleItemClick}
          animationDirection={animationDirection}
          flipSpeed={flipSpeed}
        />

        <RightSidebar
          selectedFontWeight={selectedFontWeight}
          selectedFontFamily={selectedFontFamily}
          onFontWeightChange={onFontWeightChange}
          onFontFamilyChange={onFontFamilyChange}
          selectedColumnWidth={selectedColumnWidth}
          onColumnWidthChange={onColumnWidthChange}
          selectedTextAlignment={selectedTextAlignment}
          selectedFontSize={selectedFontSize}
          selectedTemplate={selectedTemplate!}
          selectedTripType={selectedTemplate?.tripType!}
          selectedDateFormat={selectedDateFormat}
          cellLabel={cellLabel}
          isHeader={isHeader}
          alternateFontColor={selectedTemplate?.draft?.alternateRow?.color || "#FFFFFF"}
          alternateBgColor={selectedTemplate?.draft?.alternateRow?.background || "#000000"}
          selectedPageBgColor={selectedPageBgColor}
          selectedLabel={selectedLabel}
          onTripTypeChange={(tripType: string) => {
            const template: any = { ...selectedTemplate };
            template.tripType = tripType;
            setSelectedTemplate(template);
          }}
          onTextAlign={onTextAlign}
          onFontSizeChange={onFontSizeChange}
          showFlightsInlineToggle={showFlightsInlineToggle}
          onDateFormatChange={onDateFormatChange}
          onPageBgColorChange={onPageBgColorChange}
          onFontColorChange={onFontColorChange}
          onBgColorChange={onBgColorChange}
          onLabelChange={onLabelChange}
          schemaFields={schemaFields}
          templateColumns={selectedTemplate?.draft?.columns?.map((col) => col.field) || []}
          onColumnsChange={handleColumnsChange}
          selectedField={selectedField}
          selectedBgColor={selectedBgColor}
          selectedFontColor={selectedFontColor}
          animationDirection={animationDirection}
          onAnimationDirectionChange={(direction) => setAnimationDirection(direction)}
          flipSpeed={flipSpeed}
          onFlipSpeedChange={setFlipSpeed}
        />
      </div>
    </div>
  );
};

export default App;
